import React, { useState, useRef } from 'react';
import debounce from 'lodash/debounce';
import { RangeSlider } from '../../../components';
import IconPlus from '../IconPlus/IconPlus';
import css from './NearMeFilter.module.css';

const NearMeFilter = ({ onSubmit }) => {
  const [distance, setDistance] = useState(10);
  const [active, setActive] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [locationLabel, setLocationLabel] = useState('');
  const [showPostcode, setShowPostcode] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [postcodeError, setPostcodeError] = useState(null);
  const coordsRef = useRef(null);
  const distanceRef = useRef(10);
  const activeRef = useRef(false);

  const applyFilter = (lat, lng, miles) => {
    const latDeg = miles / 69.0;
    const lngDeg = miles / (69.0 * Math.cos(lat * Math.PI / 180));
    const bounds = `${lat + latDeg},${lng + lngDeg},${lat - latDeg},${lng - lngDeg}`;
    onSubmit({ bounds, origin: `${lat},${lng}` });
  };

  const applyFilterDebounced = useRef(
    debounce((lat, lng, miles) => {
      applyFilter(lat, lng, miles);
    }, 400)
  ).current;

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes?lon=${lng}&lat=${lat}`);
      const data = await res.json();
      if (data.status === 200 && data.result && data.result.length > 0) {
        return data.result[0].postcode.slice(0, 4).trim();
      }
    } catch (e) {}
    return 'your location';
  };

  const handleLocate = () => {
    if (!navigator.geolocation) {
      setShowPostcode(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async position => {
        const { latitude, longitude } = position.coords;
        coordsRef.current = { lat: latitude, lng: longitude };
        const label = await reverseGeocode(latitude, longitude);
        setLocationLabel(label);
        setActive(true);
        activeRef.current = true;
        setShowPostcode(false);
        applyFilter(latitude, longitude, distanceRef.current);
      },
      () => {
        setShowPostcode(true);
      }
    );
  };

  const handlePostcodeSubmit = async () => {
    const clean = postcode.trim().replace(/\s/g, '');
    if (!clean) return;
    setPostcodeError(null);
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${clean}`);
      const data = await res.json();
      if (data.status === 200) {
        const { latitude, longitude } = data.result;
        coordsRef.current = { lat: latitude, lng: longitude };
        setLocationLabel(postcode.trim().toUpperCase().slice(0, 3));
        setActive(true);
        activeRef.current = true;
        setShowPostcode(false);
        applyFilter(latitude, longitude, distanceRef.current);
      } else {
        setPostcodeError('Postcode not found. Please try again.');
      }
    } catch (e) {
      setPostcodeError('Could not look up postcode. Please try again.');
    }
  };

  const handleSliderChange = handles => {
    const miles = handles[1];
    setDistance(miles);
    distanceRef.current = miles;
    if (activeRef.current && coordsRef.current) {
      applyFilterDebounced(coordsRef.current.lat, coordsRef.current.lng, miles);
    }
  };

  const handleInputChange = e => {
    const val = e.target.value;
    if (val === '') {
      setDistance('');
      return;
    }
    const miles = Math.min(50, Math.max(1, Number(val) || 1));
    setDistance(miles);
    distanceRef.current = miles;
    if (activeRef.current && coordsRef.current) {
      applyFilter(coordsRef.current.lat, coordsRef.current.lng, miles);
    }
  };

  const handleInputBlur = () => {
    if (distance === '' || distance < 1) {
      setDistance(1);
      distanceRef.current = 1;
      if (activeRef.current && coordsRef.current) {
        applyFilter(coordsRef.current.lat, coordsRef.current.lng, 1);
      }
    }
  };

  const handleClear = () => {
    setActive(false);
    activeRef.current = false;
    setDistance(10);
    distanceRef.current = 10;
    setPostcode('');
    setLocationLabel('');
    setShowPostcode(false);
    setPostcodeError(null);
    coordsRef.current = null;
    onSubmit({ bounds: undefined, origin: undefined });
  };

  return (
    <div className={css.root}>
      <div className={css.filterHeader}>
        <button type="button" className={css.labelButton} onClick={() => setIsOpen(o => !o)}>
          <span className={css.labelButtonContent}>
            <span className={css.labelWrapper}>
              <span className={css.label}>Near me (miles)</span>
            </span>
            <span className={css.openSign}>
              <IconPlus isOpen={isOpen} isSelected={active} />
            </span>
          </span>
        </button>
      </div>

      {isOpen && (
        <div className={css.formWrapper}>
          <button
            className={active ? css.locateButtonActive : css.locateButton}
            onClick={handleLocate}
            type="button"
          >
            {active ? `📍 ${locationLabel}` : '📍 Use my location'}
          </button>

          {showPostcode && (
            <div className={css.postcodeBlock}>
              <p className={css.postcodeLabel}>Enter your postcode:</p>
              <div className={css.postcodeRow}>
                <input
                  className={css.postcodeInput}
                  type="text"
                  placeholder="e.g. SW1A 1AA"
                  value={postcode}
                  onChange={e => setPostcode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handlePostcodeSubmit()}
                />
                <button className={css.postcodeButton} onClick={handlePostcodeSubmit} type="button">
                  Go
                </button>
              </div>
              {postcodeError && <p className={css.errorText}>{postcodeError}</p>}
            </div>
          )}

          {active && (
            <div className={css.sliderWrapper}>
              <div className={css.inputsWrapper}>
                <input className={css.valueInput} type="number" value={0} disabled />
                <span className={css.priceSeparator}>-</span>
                <input
                  className={css.valueInput}
                  type="number"
                  min="1"
                  max="50"
                  value={distance}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                />
              </div>
              <RangeSlider
                min={0}
                max={50}
                step={1}
                handles={[0, distance === '' ? 1 : distance]}
                onChange={handleSliderChange}
              />
              <button className={css.clearButton} onClick={handleClear} type="button">
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NearMeFilter;
